import { Controller, useForm, useFieldArray } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Icon, Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { LabeledElement } from 'components/form-elements/labels/LabeledElement';
import { LabeledTimePicker } from 'components/form-elements/pickers/LabeledTimePicker';
import { LabeledWeekdayPicker } from 'components/form-elements/pickers/LabeledWeekdayPicker';
import { initialTime } from 'components/../utils/timeAndDate';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetTimeProfiles, useUpdateTimeProfile } from 'services/2n/time-profiles';
import { useUserPermissions } from 'services/user/permissions';
import { mapSchedulesToTimeProfiles } from 'services/2n/time-profiles/mapData';
import { PageLoader } from 'components/loaders/PageLoader';
import { DeleteTimeProfile } from '../DeleteTimeProfile';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import styles from './styles.module.scss';
import timeProfileFormSchema from './validation';

export const TimeProfileForm = () => {

  const timeProfiles = useGetTimeProfiles();
  const { selected, updateSelected } = useAccessManagement2NModal();
  const { checkPermission } = useUserPermissions();
  const isAdmin = checkPermission('admin-users', 'create');
  const isSystemAdmin = checkPermission('system-admin', 'create');
  const selectedTimeProfile = selected.timeProfile;
  const selectedTimeProfileName = (selectedTimeProfile && selectedTimeProfile.Name) || '';

  const selectedTimeProfiles = selectedTimeProfile
    ? mapSchedulesToTimeProfiles(selectedTimeProfile)
    : [];

  const { control, handleSubmit, getValues, watch, formState, reset } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(timeProfileFormSchema),
    defaultValues: {
      name: selectedTimeProfileName,
      timeProfiles: selectedTimeProfiles && selectedTimeProfiles.length > 0
        ? selectedTimeProfiles
        : [{ startTime: initialTime, endTime: initialTime, days: [] }],
    },
  });

  useEffect(() => {
    if (!selectedTimeProfile) {
      reset({
        name: '',
        timeProfiles: [{ startTime: initialTime, endTime: initialTime, days: [] }],
      });
      return;
    }

    const nextSchedules = selectedTimeProfile
      ? mapSchedulesToTimeProfiles(selectedTimeProfile)
      : [];

    reset({
      name: (selectedTimeProfile && (selectedTimeProfile.Name || selectedTimeProfile.name)) || '',
      timeProfiles: nextSchedules.length > 0
        ? nextSchedules
        : [{ startTime: initialTime, endTime: initialTime, days: [] }],
    });
  }, [selectedTimeProfile, reset]);

  const { errors } = formState || {};
  const updateTimeProfileMutation = useUpdateTimeProfile();
  const isUpdating = updateTimeProfileMutation && updateTimeProfileMutation.isLoading;

  const { fields: timeProfileFields, append: appendTimeProfile, remove: removeTimeProfile } = useFieldArray({
    control,
    name: 'timeProfiles',
  });

  const watchedTimeProfiles = watch('timeProfiles');

  const allDaysUsed = (watchedTimeProfiles || [])
    .reduce((acc, profile) => acc.concat((profile && profile.days) || []), []).length >= 7;

  const onSubmit = async (data) => {
    const selectedTimeProfileId = selectedTimeProfile && (selectedTimeProfile.Id || selectedTimeProfile.id);
    if (!selectedTimeProfileId) return;

    await updateTimeProfileMutation.mutateAsync({
      id: selectedTimeProfileId,
      timeProfile: {
        name: data.name,
        timeProfiles: data.timeProfiles,
      },
    });
  };

  function onDayClick(index) {
    return (day) => {
      const days = getValues(`timeProfiles.${index}.days`) || [];
      if (days.includes(day)) {
        return days.filter(selectedDay => selectedDay !== day);
      }
      return [...days, day];
    };
  }

  if (timeProfiles.isLoading) return <div className={styles.container}><PageLoader /></div>;
  
  return (
    <>
      <Brikk.Heading
        heading="editTimeProfile"
      />
      <ShouldRender condition={!!selectedTimeProfileName}>
        <Text
          element="h2"
          thin
          xl
          size={20}
        >
          {String(selectedTimeProfile && watch('name')) || ''}
        </Text>
      </ShouldRender>
      {selectedTimeProfile ? (
        <Grid
          noPadding
          oneColumn
        >
          <Form
            className={styles.formContainer}
            onSubmit={handleSubmit(onSubmit)}
          >

            <div className={styles.formWrapper}>
              <Grid
                noPadding
                oneColumn
              >
                <div className={styles.form}>
             
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <LabeledInput
                        labelIntl="timeProfileName"
                        id="name"
                        value={field.value}
                        placeholder={isAdmin && !isSystemAdmin ? 'GH-XXXXXXX' : ''}
                        onChange={(event) => {
                          if (isAdmin && !isSystemAdmin) {
                            const inputValue = event.target.value;
                            if (inputValue === '' || inputValue.toUpperCase() === 'GH-') {
                              field.onChange('');
                              return;
                            }
                            if (!inputValue.toUpperCase().startsWith('GH-')) {
                              field.onChange(`GH-${inputValue}`);
                            } else {
                              field.onChange(inputValue);
                            }
                          } else {
                            field.onChange(event.target.value);
                          }
                        }}
                        hasError={!!(errors && errors.name)}
                      />
                    )}
                  />
                  <ShouldRender condition={errors && errors.name}>
                    <Message negative>
                      <Message.Content>
                        <Text
                          error
                          intl={errors.name && errors.name.message}
                        />
                      </Message.Content>
                    </Message>
                  </ShouldRender>

                  <LabeledElement label="timeProfile">
                    {timeProfileFields.map((profileField, index) => (
                      <div
                        key={profileField.id}
                        className={styles.timeProfileEntry}
                      >
                        <Grid
                          noPadding
                          className={styles.timePickers}
                        >
                          <Controller
                            name={`timeProfiles.${index}.startTime`}
                            control={control}
                            render={({ field }) => (
                              <LabeledTimePicker
                                title="startTime"
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                timeInterval={15}
                                className={styles.timeProfileWrapper}
                                allowManualInput
                              />
                            )}
                          />
                          <ShouldRender condition={errors
                           && errors.timeProfiles
                           && errors.timeProfiles[index]
                           && errors.timeProfiles[index].startTime
                           && errors.timeProfiles[index].startTime.message}
                          >
                            <Message negative>
                              <Message.Content>
                                <Text
                                  error
                                  intl={errors
                                   && errors.timeProfiles
                                   && errors.timeProfiles[index]
                                   && errors.timeProfiles[index].startTime
                                   && errors.timeProfiles[index].startTime.message}
                                />
                              </Message.Content>
                            </Message>
                          </ShouldRender>
                          <Controller
                            name={`timeProfiles.${index}.endTime`}
                            control={control}
                            render={({ field }) => (
                              <LabeledTimePicker
                                title="endTime"
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                timeInterval={15}
                                className={styles.timeProfileWrapper}
                                allowManualInput
                              />
                            )}
                          />
                        </Grid>
                        <ShouldRender condition={errors
                         && errors.timeProfiles
                         && errors.timeProfiles[index]
                         && errors.timeProfiles[index].endTime
                         && errors.timeProfiles[index].endTime.message}
                        >
                          <Message negative>
                            <Message.Content>
                              <Text
                                error
                                intl={errors
                                 && errors.timeProfiles
                                 && errors.timeProfiles[index]
                                 && errors.timeProfiles[index].endTime
                                 && errors.timeProfiles[index].endTime.message}
                              />
                            </Message.Content>
                          </Message>
                        </ShouldRender>

                        <Controller
                          name={`timeProfiles.${index}.days`}
                          control={control}
                          render={({ field }) => {
                            
                            const disabledDays = (watchedTimeProfiles || [])
                              .reduce((acc, profile, profileIndex) => {
                                if (profileIndex === index || !(profile && profile.days)) return acc;
                                return acc.concat(profile.days);
                              }, []);

                            return (
                              <>
                                <LabeledWeekdayPicker
                                  title="daysOfTheWeek"
                                  selectedDays={field.value}
                                  disabledDays={disabledDays}
                                  onClick={(e) => field.onChange(onDayClick(index)(parseInt(e.target.value, 10)))}
                                  className={styles.weekdays}
                                />
                                {(() => {
                                  const errorDayMessages = errors
                                   && errors.timeProfiles && errors.timeProfiles[index]
                                   && errors.timeProfiles[index].days
                                   && errors.timeProfiles[index].days.message;
                                
                                  return (
                                    <ShouldRender condition={!!errorDayMessages}>
                                      <Message negative>
                                        <Message.Content>
                                          <Text
                                            error
                                            intl={errorDayMessages}
                                          />
                                        </Message.Content>
                                      </Message>
                                    </ShouldRender>
                                  );
                                })()}
                              </>
                            );
                          }}
                        />

                        <ShouldRender condition={timeProfileFields.length > 1}>
                          <Button
                            type="button"
                            size="mini"
                            onClick={() => removeTimeProfile(index)}
                            className={styles.removeTimeProfile}
                          >
                            <Icon name="minus" />
                          </Button>
                        </ShouldRender>
                      </div>
                    ))}

                    <ShouldRender condition={!allDaysUsed}>
                      <Button
                        type="button"
                        size="mini"
                        onClick={() => appendTimeProfile({ startTime: initialTime, endTime: initialTime, days: [] })}
                        className={styles.addTimeProfile}
                      >
                        <Icon name="plus" />
                      </Button>
                    </ShouldRender>
                  </LabeledElement>

                </div>
              </Grid>
            </div>

            <div className={styles.formButtons}>
              <Button
                basic
                name="cancel-time-profile-details"
                type="button"
                disabled={isUpdating}
                onClick={() => updateSelected('timeProfile', null)}
              >
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                type="submit"
                name="submit-time-profile-details"
                disabled={isUpdating}
                loading={isUpdating}
              >
                <FormattedMessage id="saveChanges" />
              </Button>
            </div>
          </Form>
          
          <ShouldRender condition={selectedTimeProfile}>
            <DeleteTimeProfile />
          </ShouldRender>

        </Grid>
      ) : (
        <Text
          secondary
          center
          intl="noTimeProfileSelected"
          size={16}
        />
      )}
    </>
  );
};

